// apps/web/src/pages/BankListPage.tsx
// Phase 2 实现 — 题库列表页面

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useState } from "react";
import toast from "react-hot-toast";
import { listBanks, mergeBanksApi } from "@/api/banks";
import BankCard from "@components/bank/BankCard";

function BankSkeleton() {
  return (
    <div className="bg-white rounded-xl shadow-card p-4 space-y-3">
      <div className="h-5 w-2/3 bg-gray-200 rounded animate-pulse" />
      <div className="flex gap-3">
        <div className="h-4 w-10 bg-gray-200 rounded animate-pulse" />
        <div className="h-4 w-16 bg-gray-200 rounded animate-pulse" />
      </div>
    </div>
  );
}

export default function BankListPage() {
  const queryClient = useQueryClient();
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeName, setMergeName] = useState("");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["banks"],
    queryFn: () => listBanks(),
  });

  const banks = data?.items ?? [];

  const mergeMutation = useMutation({
    mutationFn: () => mergeBanksApi({ bank_ids: selectedIds, name: mergeName.trim() }),
    onSuccess: (result) => {
      toast.success(`合并完成，新增 ${result.merged_count} 道题`);
      queryClient.invalidateQueries({ queryKey: ["banks"] });
      setShowMergeDialog(false);
      setSelectionMode(false);
      setSelectedIds([]);
      setMergeName("");
    },
    onError: (err: Error) => toast.error(err.message ?? "合并失败"),
  });

  function toggleBank(bankId: string) {
    setSelectedIds((ids) => ids.includes(bankId) ? ids.filter((id) => id !== bankId) : [...ids, bankId]);
  }

  function cancelSelection() {
    setSelectionMode(false);
    setSelectedIds([]);
  }

  return (
    <div className="p-4 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">我的题库</h1>
        <div className="flex items-center gap-2">
          {banks.length >= 2 && (
            <button
              type="button"
              onClick={selectionMode ? cancelSelection : () => setSelectionMode(true)}
              className="px-3 py-2 text-sm text-primary border border-primary rounded-lg btn-tap"
            >
              {selectionMode ? "取消" : "合并题库"}
            </button>
          )}
          <Link to="/banks/new" aria-label="创建题库" className="w-10 h-10 flex items-center justify-center bg-primary text-white rounded-full shadow-card hover:bg-primary-dark transition-colors btn-tap">
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          </Link>
        </div>
      </div>

      {selectionMode && (
        <div className="mb-4 flex items-center justify-between rounded-xl bg-primary/5 px-4 py-3 text-sm">
          <span className="text-gray-600">已选择 {selectedIds.length} 个题库，原题库会保留</span>
          <button
            type="button"
            disabled={selectedIds.length < 2}
            onClick={() => { setMergeName("合并题库"); setShowMergeDialog(true); }}
            className="rounded-lg bg-primary px-3 py-1.5 font-medium text-white disabled:opacity-40"
          >合并</button>
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="text-center text-danger py-10">
          加载题库失败，请稍后重试
        </div>
      )}

      {/* Loading skeleton */}
      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Array.from({ length: 3 }).map((_, i) => <BankSkeleton key={i} />)}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && banks.length === 0 && (
        <div className="text-center py-16">
          <svg viewBox="0 0 24 24" className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <line x1="12" y1="8" x2="12" y2="16" />
            <line x1="8" y1="12" x2="16" y2="12" />
          </svg>
          <p className="text-gray-400 text-sm">还没有题库，点击 + 创建</p>
        </div>
      )}

      {/* Bank list */}
      {!isLoading && !isError && banks.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {banks.map((bank) => (
            <BankCard
              key={bank.id}
              bank={bank}
              selectionMode={selectionMode}
              selected={selectedIds.includes(bank.id)}
              onToggle={() => toggleBank(bank.id)}
            />
          ))}
        </div>
      )}

      {showMergeDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-6">
          <form
            onSubmit={(event) => { event.preventDefault(); if (mergeName.trim()) mergeMutation.mutate(); }}
            className="w-full max-w-sm rounded-xl bg-white p-6 shadow-card-hover"
          >
            <h2 className="text-base font-bold text-gray-900">合并题库</h2>
            <p className="mt-2 text-sm text-gray-500">重复题目会自动跳过，原题库不会被修改。</p>
            <input
              autoFocus
              value={mergeName}
              onChange={(event) => setMergeName(event.target.value)}
              maxLength={100}
              placeholder="请输入合并后的题库名称"
              className="mt-4 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
            />
            <div className="mt-5 flex gap-3">
              <button type="button" onClick={() => setShowMergeDialog(false)} className="flex-1 rounded-lg border border-gray-200 py-2.5 text-sm text-gray-600">取消</button>
              <button type="submit" disabled={!mergeName.trim() || mergeMutation.isPending} className="flex-1 rounded-lg bg-primary py-2.5 text-sm font-medium text-white disabled:opacity-50">
                {mergeMutation.isPending ? "合并中..." : "确认合并"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
